/**
 * 画像処理パイプライン (ワンショット)。
 *
 * フロー: validate → decode → crop(4:3) → enhance → resize(<=2560x1920) → encode
 *
 * Canvas 依存部分は `ImageOps` として差し込み可能にしており、ユニットテストでは
 * モックを注入して純粋ロジックのみ検証する (実ブラウザ動作は Wave 5 E2E)。
 */

import type { CarBoundingBox } from '@shared/types';

import { cropTo4x3 as defaultCrop, smartCropTo4x3 as defaultSmartCrop } from './crop';
import { autoEnhance as defaultEnhance } from './enhance';
import { encodeJpegWithSizeCap, encodePng as defaultEncodePng } from './encode';
import { resizeToSpec as defaultResize } from './resize';
import { CARSENSOR_SPEC, type ProcessOptions, type ProcessedImage, type Result } from './types';
import { validateDimensions, validateFile } from './validate';

// ===== ops (依存性注入ポイント) =========================================

export interface ImageOps {
  readonly decode: (file: File) => Promise<ImageBitmap>;
  readonly cropTo4x3: (bitmap: ImageBitmap) => Promise<ImageBitmap>;
  readonly smartCropTo4x3: (
    bitmap: ImageBitmap,
    bbox: CarBoundingBox,
    targetFill?: number,
  ) => Promise<ImageBitmap>;
  readonly autoEnhance: (bitmap: ImageBitmap) => Promise<ImageBitmap>;
  readonly resizeToSpec: (bitmap: ImageBitmap) => Promise<ImageBitmap>;
  readonly encodeJpeg: (bitmap: ImageBitmap, quality: number) => Promise<Blob>;
  readonly encodePng: (bitmap: ImageBitmap) => Promise<Blob>;
  readonly now: () => number;
}

function browserDefaultOps(): ImageOps {
  return {
    decode: (file) => createImageBitmap(file),
    cropTo4x3: defaultCrop,
    smartCropTo4x3: defaultSmartCrop,
    autoEnhance: defaultEnhance,
    resizeToSpec: defaultResize,
    encodeJpeg: encodeJpegWithSizeCap,
    encodePng: defaultEncodePng,
    now: () => performance.now(),
  };
}

// ===== processOne ======================================================

export async function processOne(
  file: File,
  options: ProcessOptions = {},
  ops: Partial<ImageOps> = {},
): Promise<Result<ProcessedImage>> {
  const merged: ImageOps = { ...browserDefaultOps(), ...ops };
  const started = merged.now();

  const fileCheck = validateFile(file);
  if (!fileCheck.ok) return fileCheck;

  let bitmap: ImageBitmap;
  try {
    bitmap = await merged.decode(file);
  } catch (err) {
    return {
      ok: false,
      error: {
        code: 'UNSUPPORTED_FORMAT',
        message: `${file.name}: デコードに失敗しました (${String(err)})`,
        file: file.name,
      },
    };
  }

  const dimCheck = validateDimensions(bitmap.width, bitmap.height, file.name);
  if (!dimCheck.ok) return dimCheck;

  const cropped = await cropDispatch(bitmap, options, merged);
  const enhanced = options.autoEnhance === false ? cropped : await merged.autoEnhance(cropped);
  const resized = await merged.resizeToSpec(enhanced);

  const useJpeg = options.outputPng !== true;
  const blob = useJpeg
    ? await merged.encodeJpeg(resized, options.quality ?? CARSENSOR_SPEC.JPEG_QUALITY)
    : await merged.encodePng(resized);

  return {
    ok: true,
    value: {
      blob,
      width: resized.width,
      height: resized.height,
      mimeType: useJpeg ? 'image/jpeg' : 'image/png',
      processingTimeMs: Math.round(merged.now() - started),
    },
  };
}

/**
 * Wave 19: smart crop と通常 crop の分岐。lint complexity 対策で関数分離。
 * Wave 21.4: 実行ログを追加。田中さん本番検証時にブラウザ DevTools で
 * smart crop が実際に呼ばれているか、どの bbox で処理されたかを確認可能に。
 */
async function cropDispatch(
  bitmap: ImageBitmap,
  options: ProcessOptions,
  ops: ImageOps,
): Promise<ImageBitmap> {
  if (options.smartCropBbox) {
    const bbox = options.smartCropBbox;
    // eslint-disable-next-line no-console
    console.debug(
      `[pipeline] smart-crop w=${bbox.w.toFixed(2)} h=${bbox.h.toFixed(2)} (${bitmap.width}x${bitmap.height})`,
    );
    const out = await ops.smartCropTo4x3(bitmap, bbox, options.smartCropFill);
    // eslint-disable-next-line no-console
    console.debug(`[pipeline] smart-crop done: ${out.width}x${out.height}`);
    return out;
  }
  // eslint-disable-next-line no-console
  console.debug(`[pipeline] center-crop (${bitmap.width}x${bitmap.height})`);
  return ops.cropTo4x3(bitmap);
}
